import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, api } from '../utils/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const userData = await authAPI.getProfile();
        setUser(userData);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    localStorage.setItem('token', response.access_token);
    setUser(response.user);
    return response;
  };

  const register = async (email, password, name) => {
    const response = await authAPI.register(email, password, name);
    localStorage.setItem('token', response.access_token);
    setUser(response.user);
    return response;
  };

  // Функция отправки кода подтверждения
  const sendVerificationCode = async (email) => {
    try {
      console.log('Sending verification code to:', email);
      
      const response = await api.post('/auth/send-verification-code', { 
        email: email 
      });
      
      console.log('Verification code sent successfully');
      return response.data;
      
    } catch (error) {
      console.error('Send verification code error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.detail 
        || error.response?.data?.message 
        || 'Ошибка отправки кода подтверждения';
      
      throw new Error(errorMessage);
    }
  };

  // Функция подтверждения email
  const verifyEmail = async (email, code) => {
    try {
      console.log('Verifying email:', email, 'with code:', code);
      
      const response = await api.post('/auth/verify-email', { 
        email: email,
        code: code 
      });
      
      console.log('Email verified successfully');
      return response.data;
      
    } catch (error) {
      console.error('Verify email error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.detail 
        || error.response?.data?.message 
        || 'Ошибка подтверждения email';
      
      throw new Error(errorMessage);
    }
  };

  // Функция запроса сброса пароля
  const requestPasswordReset = async (email) => {
    try {
      console.log('Requesting password reset for:', email);
      
      const response = await api.post('/auth/forgot-password', { 
        email: email 
      });
      
      console.log('Password reset requested successfully');
      return response.data;
      
    } catch (error) {
      console.error('Password reset request error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.detail 
        || error.response?.data?.message 
        || 'Ошибка запроса сброса пароля';
      
      throw new Error(errorMessage);
    }
  };

  // Функция сброса пароля с кодом подтверждения
  const resetPassword = async (email, code, newPassword) => {
    try {
      console.log('Resetting password for:', email);
      
      const response = await api.post('/auth/reset-password', { 
        email: email,
        code: code,
        new_password: newPassword
      });
      
      console.log('Password reset successfully');
      return response.data;
      
    } catch (error) {
      console.error('Password reset error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.detail 
        || error.response?.data?.message 
        || 'Ошибка сброса пароля';
      
      throw new Error(errorMessage);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    checkAuth,
    loading,
    sendVerificationCode,
    verifyEmail,
    requestPasswordReset, // Добавьте эту функцию
    resetPassword // Добавьте эту функцию
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};